import { BsTrash } from 'react-icons/bs';
import type { ColDef } from '../../../components/ui/table';
import { GridChip, GridCellWrap, EmptyGridCell, GRID_CHIP_TONE, GRID_HINT_TOOLTIP_CLASS, CopyableGridText } from '../../../components/shared/agGridCells';
import { Button } from '../../../components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import type { FraudDecisionRow, FraudBlocklistEntry } from '../models/types';

function formatDateTime(value?: string | null) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

function CopyableCell({
  value,
  copyLabel,
  textClassName,
}: {
  value?: string | null;
  copyLabel: string;
  textClassName?: string;
}) {
  const text = value?.trim();
  if (!text) return <EmptyGridCell />;
  return (
    <CopyableGridText text={text} copyLabel={copyLabel} textClassName={textClassName} hint={text} />
  );
}

export function createDecisionColumnDefs(): ColDef<FraudDecisionRow>[] {
  return [
    {
      headerName: 'When',
      field: 'createdAt',
      width: 170,
      valueFormatter: (p) => formatDateTime(p.value),
    },
    {
      headerName: 'Decision',
      field: 'decision',
      width: 158,
      // A semantic status dot + ink label reads like a console, not a sticker. Shadow-mode verdicts
      // (recorded, not enforced) get a hollow dot, muted label and a small "shadow" caption, so an
      // enforced decision is visibly the one that actually acted.
      cellRenderer: (p: any) => {
        if (!p.value) return <EmptyGridCell />;
        const tone = String(p.value).toLowerCase();
        const shadow = Boolean(p.data?.shadowMode);
        const label = tone.charAt(0).toUpperCase() + tone.slice(1);
        return (
          <GridCellWrap className="fraud-dec-cell">
            <span className={`fraud-dec tone-${tone}${shadow ? ' is-shadow' : ''}`}>
              <span className="fraud-dec-dot" />
              <span className="fraud-dec-label">{label}</span>
            </span>
            {shadow ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="fraud-dec-shadow">shadow</span>
                </TooltipTrigger>
                <TooltipContent side="top" className={GRID_HINT_TOOLTIP_CLASS}>
                  Shadow mode — recorded, not enforced
                </TooltipContent>
              </Tooltip>
            ) : null}
          </GridCellWrap>
        );
      },
    },
    {
      headerName: 'Risk',
      field: 'score',
      width: 138,
      // Number-forward meter: the figure is the anchor, the thin bar gives it a glanceable scale.
      // Low scores stay visually calm; only review/amber and block/red carry coloured ink.
      cellRenderer: (p: any) => {
        const score = Number(p.value ?? 0);
        const tone = score >= 70 ? 'block' : score >= 40 ? 'review' : 'allow';
        const pct = Math.min(100, Math.max(0, score));
        return (
          <GridCellWrap className="fraud-score-cell">
            <span className={`fraud-score-num tone-${tone}`}>{score}</span>
            <span className="fraud-score-track">
              <span className={`fraud-score-fill tone-${tone}`} style={{ width: `${pct}%` }} />
            </span>
          </GridCellWrap>
        );
      },
    },
    { headerName: 'Merchant', field: 'merchantName', width: 140 },
    {
      headerName: 'Email',
      field: 'customerEmail',
      width: 220,
      cellRenderer: (p: any) => (
        <GridCellWrap>
          <CopyableCell value={p.value} copyLabel="Copy email" />
        </GridCellWrap>
      ),
    },
    {
      headerName: 'Amount',
      field: 'amount',
      width: 120,
      type: 'numericColumn',
      valueFormatter: (p) =>
        p.value == null ? '' : `${Number(p.value).toFixed(2)} ${p.data?.currency ?? ''}`.trim(),
    },
    {
      headerName: 'Why',
      field: 'reasons',
      flex: 1,
      minWidth: 260,
      // Single line always — the top reason truncates to fit, a compact +N badge stands for the
      // rest, and the full list is on the shared tooltip and in the row's detail dialog. Wrapping
      // every reason inline (the old autoHeight approach) let a multi-signal row grow two or three
      // rows tall and made the grid ragged; the point of this column is the headline, not the essay.
      cellRenderer: (p: any) => {
        const reasons: string[] = p.value ?? [];
        if (!reasons.length) return <EmptyGridCell />;
        const [first, ...rest] = reasons;
        return (
          <GridCellWrap className="fraud-reason-cell">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="fraud-reason-chip">{first}</span>
              </TooltipTrigger>
              <TooltipContent side="top" align="start" className={GRID_HINT_TOOLTIP_CLASS}>
                {first}
              </TooltipContent>
            </Tooltip>
            {rest.length ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="fraud-reason-chip is-more">+{rest.length}</span>
                </TooltipTrigger>
                <TooltipContent side="top" align="start" className={GRID_HINT_TOOLTIP_CLASS}>
                  <span className="flex flex-col gap-1">
                    {rest.map((reason, index) => (
                      <span key={`${reason}-${index}`}>{reason}</span>
                    ))}
                  </span>
                </TooltipContent>
              </Tooltip>
            ) : null}
          </GridCellWrap>
        );
      },
    },
    {
      headerName: 'Caller IP',
      field: 'callerIp',
      width: 190,
      // Declared IP shown underneath when it differs — that gap is itself a signal.
      cellRenderer: (p: any) => {
        const caller = (p.value as string | null | undefined)?.trim();
        const declared = (p.data?.declaredIp as string | null | undefined)?.trim();
        if (!caller && !declared) return <EmptyGridCell />;
        const showDeclared = Boolean(declared && declared !== caller);
        return (
          <GridCellWrap className={showDeclared ? 'flex-col items-start justify-center gap-0.5' : undefined}>
            {caller ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="min-w-0 truncate">{caller}</span>
                </TooltipTrigger>
                <TooltipContent side="top" align="start" className={GRID_HINT_TOOLTIP_CLASS}>
                  {caller}
                </TooltipContent>
              </Tooltip>
            ) : (
              <EmptyGridCell />
            )}
            {showDeclared ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="min-w-0 truncate text-[11px] opacity-70">{`declared: ${declared}`}</span>
                </TooltipTrigger>
                <TooltipContent side="top" align="start" className={GRID_HINT_TOOLTIP_CLASS}>
                  {declared}
                </TooltipContent>
              </Tooltip>
            ) : null}
          </GridCellWrap>
        );
      },
    },
    {
      headerName: 'Card',
      field: 'cardFingerprint',
      width: 140,
      cellRenderer: (p: any) => (
        <GridCellWrap>
          <CopyableCell value={p.value} copyLabel="Copy card" />
        </GridCellWrap>
      ),
    },
    // Transaction and order ids are in the row's detail dialog — two 200px columns of UUID here
    // only forced the grid to scroll sideways.
  ];
}

export function createBlocklistColumnDefs(
  onRemove: (entry: FraudBlocklistEntry) => void,
): ColDef<FraudBlocklistEntry>[] {
  return [
    { headerName: 'Type', field: 'entryType', width: 130 },
    {
      headerName: 'Value',
      field: 'entryValue',
      flex: 1,
      minWidth: 220,
      cellRenderer: (p: any) => (
        <GridCellWrap>
          <CopyableCell value={p.value} copyLabel="Copy value" />
        </GridCellWrap>
      ),
    },
    { headerName: 'Reason', field: 'reason', flex: 1, minWidth: 200 },
    {
      headerName: 'Status',
      field: 'active',
      width: 120,
      cellRenderer: (p: any) => (
        <GridChip
          label={p.value ? 'Blocking' : 'Expired'}
          tone={p.value ? { bg: 'rgba(255,71,87,0.15)', color: '#ff4757' } : GRID_CHIP_TONE.neutral}
        />
      ),
    },
    {
      headerName: 'Expires',
      field: 'expiresAt',
      width: 170,
      valueFormatter: (p) => (p.value ? formatDateTime(p.value) : 'Never'),
    },
    { headerName: 'Added by', field: 'addedBy', width: 150 },
    {
      headerName: '',
      colId: 'actions',
      width: 72,
      sortable: false,
      filter: false,
      // Pinned so it stays at the end, past the audit columns DataGrid appends by default.
      pinned: 'right',
      cellRenderer: (p: any) => (
        <div className="flex h-full items-center justify-end py-1" onClick={(e) => e.stopPropagation()}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 shrink-0 hover:border-destructive hover:text-destructive hover:bg-destructive/10"
                aria-label="Remove from blocklist"
                onClick={() => onRemove(p.data)}
              >
                <BsTrash />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className={GRID_HINT_TOOLTIP_CLASS}>
              Remove from blocklist
            </TooltipContent>
          </Tooltip>
        </div>
      ),
    },
  ];
}
